import {
	alpha,
	Box,
	ButtonBase,
	InputBase,
	ListItemButton,
	Stack,
	Typography,
	useTheme,
} from "@mui/material";
import { type KeyboardEvent, useState } from "react";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import type { FlatRowSelectOption } from "web_myplann/Shared/Components/FlatRow/FlatRowSelect";
import { FlatRowSelectOptionList } from "web_myplann/Shared/Components/FlatRow/FlatRowSelectOptionList";
import { MyPlannDrawer } from "web_myplann/Shared/Components/MyPlannDrawer";
import { SabCircularProgress } from "web_myplann/Shared/Progresses";

export type FlatRowComboboxProps = {
	label: string;
	value: string;
	options: FlatRowSelectOption<string>[];
	onChange: (value: string) => void;
	onBlur?: () => void;
	drawerTitle?: string;
	placeholder?: string;
	searchPlaceholder?: string;
	isLast?: boolean;
	isLoading?: boolean;
	error?: boolean;
	helperText?: string;
};

export const FlatRowCombobox = ({
	label,
	value,
	options,
	onChange,
	onBlur,
	drawerTitle,
	placeholder,
	searchPlaceholder,
	isLast,
	isLoading,
	error,
	helperText,
}: FlatRowComboboxProps) => {
	const theme = useTheme();
	const { t } = useResource();
	const [open, setOpen] = useState(false);
	const [search, setSearch] = useState("");

	const openDrawer = () => {
		setSearch(value);
		setOpen(true);
	};

	const closeDrawer = () => {
		setOpen(false);
		onBlur?.();
	};

	const commit = (next: string) => {
		onChange(next.trim());
		setOpen(false);
		onBlur?.();
	};

	const normalizedSearch = search.trim().toLowerCase();

	const filteredOptions = normalizedSearch
		? options.filter((option) =>
				option.label.toLowerCase().includes(normalizedSearch),
			)
		: options;

	const handleSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
		if (isLoading) {
			return;
		}
		if (event.key === "Enter") {
			event.preventDefault();
			commit(search);
		}
	};

	return (
		<Box>
			<ButtonBase
				onClick={openDrawer}
				sx={{
					width: "100%",
					px: 2,
					minHeight: 48,
					justifyContent: "space-between",
					borderBottom: isLast
						? "none"
						: `1px solid ${alpha(theme.palette.deprecated.grey[100], 0.6)}`,
					"&:active": { bgcolor: theme.palette.deprecated.grey[50] },
				}}
			>
				<Typography
					variant="deprecatedBody1"
					color={
						error
							? theme.palette.deprecated.error.main
							: theme.palette.deprecated.grey[500]
					}
					flexShrink={0}
					mr={2}
				>
					{label}
				</Typography>
				<Typography
					variant="deprecatedBodyMedium"
					color={
						value
							? theme.palette.deprecated.grey[800]
							: theme.palette.deprecated.grey[300]
					}
					flex={1}
					minWidth={0}
					overflow="hidden"
					whiteSpace="nowrap"
					textAlign="right"
					sx={{
						maskImage: "linear-gradient(to right, transparent 0, black 24px)",
						WebkitMaskImage:
							"linear-gradient(to right, transparent 0, black 24px)",
					}}
				>
					{value || placeholder || ""}
				</Typography>
			</ButtonBase>

			{error && helperText && (
				<Typography
					variant="deprecatedCaption"
					color={theme.palette.deprecated.error.main}
					px={2}
					pb={0.6}
				>
					{helperText}
				</Typography>
			)}

			<MyPlannDrawer
				anchor="bottom"
				open={open}
				onClose={closeDrawer}
				title={drawerTitle ?? label}
				closeButton
			>
				<Stack
					px={2}
					py={1.4}
					flexShrink={0}
					borderBottom={`1px solid ${alpha(theme.palette.deprecated.grey[100], 0.6)}`}
				>
					<InputBase
						value={search}
						onChange={(event) => setSearch(event.target.value)}
						onKeyDown={handleSearchKeyDown}
						placeholder={searchPlaceholder ?? placeholder ?? label}
						autoFocus
						fullWidth
						sx={{
							fontSize: 14,
							fontWeight: 500,
							color: theme.palette.deprecated.grey[900],
							"& input::placeholder": {
								color: theme.palette.deprecated.grey[300],
								fontWeight: 400,
								opacity: 1,
							},
						}}
					/>
				</Stack>

				<Stack flex="1 1 auto" minHeight={0} overflow="auto">
					{isLoading ? (
						<Stack alignItems="center" justifyContent="center" py={3}>
							<SabCircularProgress size="xl" />
						</Stack>
					) : (
						<>
							<ListItemButton
								onClick={() => commit(search)}
								sx={{
									px: 2,
									py: 1.4,
									borderBottom: `1px solid ${alpha(theme.palette.deprecated.grey[100], 0.6)}`,
								}}
							>
								<Typography
									variant="deprecatedBody1"
									color={theme.palette.primary.main}
								>
									{t(Resources.Portfolio.AddShared.Combobox.useValue, {
										value: search.trim(),
									})}
								</Typography>
							</ListItemButton>

							<FlatRowSelectOptionList
								options={filteredOptions}
								value={value}
								onSelect={commit}
							/>
						</>
					)}
				</Stack>
			</MyPlannDrawer>
		</Box>
	);
};
